/**
 * Collection-page sidebar (snippets/cat-sidebar.liquid inside sections/collection-browse.liquid).
 *
 *  - Context: the server renders a group's or leaf's CANONICAL placement — Liquid cannot read
 *    the #from=<venue>&zone=<zone> fragment. A visitor who walked in through another venue
 *    gets that venue's sidebar from ?view=side-tree (every placement of the node), picked by
 *    zone, then by venue. Failure leaves the canonical one, which is still a correct place.
 *  - Zone heading button «Show all subcategories» (every level) fetches the leaves of every group that is not the page from ?view=side-leaves.
 *  - The current block is scrolled into view inside the sidebar's own scroll.
 *  - Below 992px the tree folds behind the «Categories» toggle.
 *
 * Context rides in the URL FRAGMENT, never the query (theme/docs/plans/gsc-coverage-2026-07.md).
 */
(function () {
  var side = document.querySelector('[data-cat-side]');
  if (!side) return;
  var treeBox = side.querySelector('[data-cat-side-tree]');
  if (!treeBox) return;

  var params = new URLSearchParams(location.hash.slice(1));
  var from = params.get('from') || '';
  var zone = params.get('zone') || '';

  // Only names the taxonomy knows are acted on — the maps are on the page already for
  // breadcrumb-context.js (generated from venue-zone-map.liquid).
  function readMap(sel) {
    var el = document.querySelector(sel);
    if (!el) return {};
    try { return JSON.parse(el.textContent); } catch (e) { return {}; }
  }
  var venueMap = readMap('[data-bq-venue-map]');
  var zoneMap = readMap('[data-bq-zone-map]');
  if (from && !venueMap[from]) from = '';
  if (zone && !zoneMap[zone]) zone = '';

  function ctxFragment(venue, z) {
    var pairs = [];
    if (venue) pairs.push('from=' + encodeURIComponent(venue));
    if (z) pairs.push('zone=' + encodeURIComponent(z));
    return pairs.length ? '#' + pairs.join('&') : '';
  }

  // ── Show / hide every group's leaves, all zones at once ─────────────────────────
  // The leaves of groups that are not the page are not in the HTML (so neighbouring pages do not
  // all serve the same tree — docs/plans/category-sidebar-layout.md); each group leaves an empty
  // slot pointing at ?view=side-leaves, fetched the first time the zone is unfolded.
  function loadLeaves(box) {
    if (box.getAttribute('data-loaded')) return;
    box.setAttribute('data-loaded', '1');
    box.classList.add('is-loading');
    var ctx = ctxFragment(box.getAttribute('data-venue'), box.getAttribute('data-zone'));
    fetch(box.getAttribute('data-cat-leaves'), { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.text() : Promise.reject(r.status); })
      .then(function (html) {
        box.innerHTML = html;
        // The fragment view knows no zone, so its links carry no context; give them this zone's.
        if (ctx) {
          box.querySelectorAll('a[href]').forEach(function (a) {
            var href = a.getAttribute('href');
            if (href.indexOf('#') === -1) a.setAttribute('href', href + ctx);
          });
        }
      })
      .catch(function () { box.removeAttribute('data-loaded'); box.hidden = true; })
      .then(function () { box.classList.remove('is-loading'); });
  }

  // The choice survives page changes (owner, 2026-10-23): a visitor who unfolded everything keeps
  // it unfolded on the next collection page. localStorage, guarded — a private window or blocked
  // storage simply starts folded every time.
  var SUBS_KEY = 'bq-cat-subs-open';
  function setSubs(open, remember) {
    side.querySelectorAll('[data-cat-subs-toggle]').forEach(function (b) {
      b.setAttribute('aria-expanded', String(open));
      b.textContent = b.getAttribute(open ? 'data-label-hide' : 'data-label-show');
    });
    side.querySelectorAll('[data-cat-leaves]').forEach(function (box) {
      box.hidden = !open;
      if (open) loadLeaves(box);
    });
    if (remember) {
      try { localStorage.setItem(SUBS_KEY, open ? '1' : '0'); } catch (err) { /* storage blocked */ }
    }
  }
  function restoreSubs() {
    var saved = null;
    try { saved = localStorage.getItem(SUBS_KEY); } catch (err) { /* storage blocked */ }
    if (saved === '1') setSubs(true, false);
  }

  side.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-cat-subs-toggle]');
    if (!btn || !side.contains(btn)) return;
    // One switch for the whole column (owner, 2026-10-23): any zone's button unfolds or folds
    // every zone, and all the buttons flip their label together.
    setSubs(btn.getAttribute('aria-expanded') !== 'true', true);
  });

  // ── Keep the current block in view inside the sidebar's own scroll ─────
  var mq = window.matchMedia('(max-width: 991px)');
  function revealCurrent() {
    if (mq.matches) return;
    var cur = treeBox.querySelector('.cat-block.is-current') || treeBox.querySelector('.cat-block.is-active');
    if (!cur) return;
    var top = cur.getBoundingClientRect().top - side.getBoundingClientRect().top + side.scrollTop;
    if (top > side.clientHeight * 0.6) side.scrollTop = top - side.clientHeight / 3;
  }

  // ── Another venue's placement ──────────────────────────────────────────
  var mode = side.getAttribute('data-mode');
  var baseVenue = side.getAttribute('data-venue') || '';
  var baseZone = side.getAttribute('data-zone') || '';
  var foreign = (mode === 'group' || mode === 'leaf') &&
                ((zone && zone !== baseZone) || (!zone && from && from !== baseVenue));
  if (foreign) {
    fetch(location.pathname + '?view=side-tree', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.text() : Promise.reject(r.status); })
      .then(function (html) {
        var tpl = document.createElement('template');
        tpl.innerHTML = html;
        var places = tpl.content.querySelectorAll('[data-place-zone]');
        var pick = null;
        for (var i = 0; i < places.length && !pick; i++) {
          if (zone && places[i].getAttribute('data-place-zone') === zone) pick = places[i];
        }
        for (var j = 0; j < places.length && !pick; j++) {
          if (from && places[j].getAttribute('data-place-venue') === from) pick = places[j];
        }
        if (!pick || !pick.innerHTML.trim()) return;
        treeBox.innerHTML = pick.innerHTML;
        side.setAttribute('data-venue', pick.getAttribute('data-place-venue'));
        side.setAttribute('data-zone', pick.getAttribute('data-place-zone'));
      })
      .catch(function () {})
      .then(function () { restoreSubs(); revealCurrent(); });
  } else {
    restoreSubs();
    revealCurrent();
  }

  // ── Mobile toggle ──────────────────────────────────────────────────────
  var toggle = side.querySelector('[data-cat-side-toggle]');
  if (toggle) {
    toggle.addEventListener('click', function () {
      var open = !side.classList.contains('is-open');
      side.classList.toggle('is-open', open);
      toggle.setAttribute('aria-expanded', String(open));
    });
  }
})();
